import { ecspLegalComplianceProfileConfig } from '../../components/account/legal/complianceProfile/ecspComplianceProfile'
import { ecspPersonalComplianceProfileConfig } from '../../components/account/personal/complianceProfile/ecspComplianceProfile'
import { isNaturalComplianceProfileCategoryComplete } from '../../components/wizard/ESAC/form/forms'
import { isLegalComplianceProfileCategoryComplete } from '../../components/wizard/ESAC/legal/form/forms'
import { App } from '../../typings/api-enums'
import { useInvestmentProcessDataState } from '../investment-process/useInvestmentProcess'
import { isLegalProfile, isNaturalProfile } from '../investment-process/useUserProfileType'
import { useLegalAccountDataState } from './useAccountDataLegal'
import { usePersonalAccountDataState } from './useAccountDataPersonal'
import YesNoOption = App.Enums.ComplianceProfile.YesNoOption
import { EcspStatus } from '../../typings'

export function isKnowledgable(): boolean {
    if (isNaturalProfile()) {
        const investorClassification =
            usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.investor_classification
        return (
            investorClassification.expert_investor_treatment === App.Enums.ComplianceProfile.YesNoOption.YES &&
            investorClassification.self_disclosure !== null
        )
    } else if (isLegalProfile()) {
        const investorClassification =
            useLegalAccountDataState().legalProfile.compliance_profiles.ecsp.investor_classification
        return (
            investorClassification.expert_investor_treatment === App.Enums.ComplianceProfile.YesNoOption.YES &&
            investorClassification.self_disclosure === null
        )
    }
}

export function showNotEnoughInvestorClassificationWarning(): boolean {
    if (isNaturalProfile()) {
        if (
            usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.investor_classification
                .expert_investor_treatment === YesNoOption.YES &&
            usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.investor_classification
                .self_disclosure === null
        ) {
            return true
        }
    } else if (isLegalProfile()) {
        if (
            useLegalAccountDataState().legalProfile.compliance_profiles.ecsp.investor_classification
                .expert_investor_treatment === YesNoOption.YES &&
            useLegalAccountDataState().legalProfile.compliance_profiles.ecsp.investor_classification.self_disclosure ===
                null
        ) {
            return true
        }
    }
}

export function showNotEnoughExperienceWarning(): boolean {
    if (isNaturalProfile()) {
        if (isKnowledgable()) {
            return false
        }
        if (
            getPercentageExperience() <= 65 ||
            usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.experience.no_answer === true
        ) {
            return true
        }
    }
}

export function isVerifiedExpert(): boolean {
    return isNaturalProfile()
        ? usePersonalAccountDataState().naturalProfile.ecsp_status === EcspStatus.EXPERT
        : useLegalAccountDataState().legalProfile.ecsp_status === EcspStatus.EXPERT
}

export function showNotEnoughRiskAcknowledgementsWarning(): boolean {
    if (isNaturalProfile()) {
        if (isKnowledgable()) {
            return false
        }
        return (
            getPercentageRiskAcknowledgements() < 100 ||
            usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.risk.no_answer === true
        )
    } else if (isLegalProfile()) {
        if (isKnowledgable()) {
            return false
        }
        return (
            getPercentageRiskAcknowledgements(true) < 100 ||
            useLegalAccountDataState().legalProfile.compliance_profiles.ecsp.risk.no_answer === true
        )
    }
}

export function showInvestmentIsTooHighWarning(): boolean {
    if (isNaturalProfile()) {
        return (
            isKnowledgable() === false &&
            usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.financial_situation
                .loss_bearing_capacity < useInvestmentProcessDataState().investmentAmount
        )
    } else {
        return (
            isKnowledgable() === false &&
            useLegalAccountDataState().legalProfile.compliance_profiles.ecsp.financial_situation.loss_bearing_capacity <
                useInvestmentProcessDataState().investmentAmount
        )
    }
}

export const showNoFinancialSituationProvidedOrInvestmentIsTooHighWarning = (): boolean =>
    showInvestmentIsTooHighWarning()

function getPercentageLegalRiskAcknowledgements() {
    const correctAnswerMapping = {
        most_startups: [App.Enums.ComplianceProfile.ECSP.MostStartupsAnswerOption.FAIL],
        dividends: [App.Enums.ComplianceProfile.ECSP.DividendsAnswerOption.EQUITY],
        higher_interest_rates: [App.Enums.ComplianceProfile.ECSP.HigherInterestRatesAnswerOption.RISK_INVESTMENT],
        secure_investment: [App.Enums.ComplianceProfile.ECSP.SecureInvestmentAnswerOption.BOND_BRD],
        stocks_bankrupt_company: [App.Enums.ComplianceProfile.ECSP.StocksBankruptCompanyAnswerOption.LOSS],
        good_long_term_investment: [
            App.Enums.ComplianceProfile.ECSP.GoodLongTermInvestmentAnswerOption.SCATTER_INVESTMENTS,
        ],
        return_on_investment: [App.Enums.ComplianceProfile.ECSP.ReturnOnInvestmentAnswerOption.MORE_THAN_5_YEARS],
    }

    const totalQuestions = Object.keys(correctAnswerMapping).length
    const correctAnswers = Object.keys(correctAnswerMapping)?.filter((questionKey) => {
        const correctAnswers = correctAnswerMapping[questionKey]
        return correctAnswers.includes(
            useLegalAccountDataState().legalProfile.compliance_profiles.ecsp.risk[questionKey]
        )
    }).length

    return (correctAnswers / totalQuestions) * 100
}

function getPercentageExperience() {
    const correctAnswerMapping = {
        financial_education: [
            App.Enums.ComplianceProfile.ECSP.FinancialEducation.PROFESSIONAL_EXPERIENCE,
            App.Enums.ComplianceProfile.ECSP.FinancialEducation.BOTH,
            App.Enums.ComplianceProfile.ECSP.FinancialEducation.RELEVANT_DEGREE,
        ],
        bonds_knowledge: [App.Enums.ComplianceProfile.ECSP.YesNoDenyOption.YES],
        stocks_knowledge: [App.Enums.ComplianceProfile.ECSP.YesNoDenyOption.YES],
        convertible_bonds_knowledge: [App.Enums.ComplianceProfile.ECSP.YesNoDenyOption.YES],
    }

    const totalQuestions = Object.keys(correctAnswerMapping).length
    const correctAnswers = Object.keys(correctAnswerMapping).filter((questionKey) => {
        const correctAnswers = correctAnswerMapping[questionKey]
        return correctAnswers.includes(
            usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.experience[questionKey]
        )
    }).length

    return (correctAnswers / totalQuestions) * 100
}

function getPercentageRiskAcknowledgements(legal = false) {
    const correctAnswerMapping = {
        startup_investing: [App.Enums.ComplianceProfile.ECSP.StartupInvestingAnswerOption.LOSS],
        investments_outside_stock_market: [
            App.Enums.ComplianceProfile.ECSP.InvestmentsOutsideStockMarketAnswerOption.HIGH_RISK,
        ],
        subordinated_financial_instruments: [
            App.Enums.ComplianceProfile.ECSP.SubordinatedFinancialInstrumentsAnswerOption.LOSE_INVESTMENT,
        ],
        profit_participation_certificate: [
            App.Enums.ComplianceProfile.ECSP.ProfitParticipationCertificateAnswerOption.PARTICIPATE_PROFITS_LOSSES,
        ],
    }

    const totalQuestions = Object.keys(correctAnswerMapping).length
    const correctAnswers = Object.keys(correctAnswerMapping).filter((questionKey) => {
        const correctAnswers = correctAnswerMapping[questionKey]
        return correctAnswers.includes(
            legal
                ? useLegalAccountDataState().legalProfile.compliance_profiles.ecsp.risk[questionKey]
                : usePersonalAccountDataState().naturalProfile.compliance_profiles.ecsp.risk[questionKey]
        )
    }).length

    return (correctAnswers / totalQuestions) * 100
}

export async function isEcspComplianceProfileInformationMissingForInvestment() {
    if (isNaturalProfile()) {
        return (
            !(await isNaturalComplianceProfileCategoryComplete(
                ecspPersonalComplianceProfileConfig['Investor Classification'].validationKey,
                ecspPersonalComplianceProfileConfig['Investor Classification'].denyKey
            )) ||
            !(await isNaturalComplianceProfileCategoryComplete(
                ecspPersonalComplianceProfileConfig['Experience'].validationKey,
                ecspPersonalComplianceProfileConfig['Experience'].denyKey
            )) ||
            !(await isNaturalComplianceProfileCategoryComplete(
                ecspPersonalComplianceProfileConfig['Risk'].validationKey,
                ecspPersonalComplianceProfileConfig['Risk'].denyKey
            ))
        )
    } else if (isLegalProfile()) {
        return (
            !(await isLegalComplianceProfileCategoryComplete(
                ecspLegalComplianceProfileConfig['Investor Classification'].validationKey,
                ecspLegalComplianceProfileConfig['Investor Classification'].denyKey
            )) ||
            !(await isLegalComplianceProfileCategoryComplete(
                ecspLegalComplianceProfileConfig['Risk'].validationKey,
                ecspLegalComplianceProfileConfig['Risk'].denyKey
            ))
        )
    }
}
